import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { LivePhoneScreenRequest, LivePhoneScreenResponse } from "@/utils/a2a/types";
import { useElevenLabsScreeningAgent } from "@/hooks/tools/useElevenLabsScreeningAgent";
import { usePhoneNumberValidation } from "../../screening/usePhoneNumberValidation";
import { useTwilioConfig } from "../../screening/useTwilioConfig";
import { useCallMonitoring } from "../../screening/useCallMonitoring";
import { PhoneScreeningState } from "../../screening/types";

// Re-export the type for backward compatibility
export type { PhoneScreeningState } from "../../screening/types";

export function usePhoneScreening() {
  const [state, setState] = useState<PhoneScreeningState>({
    status: 'idle',
    transcript: null,
    liveTranscript: null,
    summary: null,
    callId: null,
  });

  const { getAvailableVoices, voiceOrchestrator } = useElevenLabsScreeningAgent();
  const simulationIntervalRef = useRef<number | null>(null);
  const simulatedLinesRef = useRef<string[]>([]);
  const { validatePhoneNumber, formatPhoneNumber } = usePhoneNumberValidation();
  const { checkTwilioConfiguration, goToIntegrations } = useTwilioConfig();
  const { pollCallStatus, callStatusIntervalRef } = useCallMonitoring();
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (simulationIntervalRef.current) {
        window.clearInterval(simulationIntervalRef.current);
      }
    };
  }, []);

  const conductLivePhoneScreen = async (request: LivePhoneScreenRequest): Promise<LivePhoneScreenResponse | null> => {
    try {
      setState(prev => ({ ...prev, status: 'connecting' }));
      
      if (!validatePhoneNumber(request.candidatePhone)) {
        setState(prev => ({ ...prev, status: 'failed' }));
        return null;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Authentication required to use screening agent");
      }
      
      // Check if Twilio is configured
      const isTwilioConfigured = await checkTwilioConfiguration();
      
      // Try to get ElevenLabs voices
      try {
        await getAvailableVoices();
      } catch (err) {
        console.warn("ElevenLabs integration not configured properly:", err);
      }

      // Generate script text for the call
      let scriptText = `Hello ${request.candidateName}, this is an automated call regarding your application for the ${request.jobTitle} position. We would like to conduct a brief phone screening with you now. Do you have a few minutes to talk?`;
      
      let voiceId = undefined;
      if (voiceOrchestrator) {
        voiceId = voiceOrchestrator.getVoiceForContext({
          scenario: 'initial_screening',
          phase: 'introduction',
          candidateData: {
            role: request.jobTitle
          }
        });
        
        scriptText = voiceOrchestrator.enhanceText(scriptText, {
          isQuestion: true,
          emphasis: ['brief phone screening']
        });
      }

      if (isTwilioConfigured) {
        const formattedPhone = formatPhoneNumber(request.candidatePhone);
        const { data, error } = await supabase.functions.invoke('twilio-integration', {
          body: {
            action: 'initiateCall',
            candidateId: request.candidateId,
            candidateName: request.candidateName,
            candidatePhone: formattedPhone,
            jobTitle: request.jobTitle,
            voiceId,
            scriptText
          }
        });

        if (error) throw error;

        setState(prev => ({ 
          ...prev, 
          status: 'in-progress',
          liveTranscript: "Calling candidate...",
          callId: data.callId
        }));

        pollCallStatus(data.callId, setState, simulatedLinesRef);

        return {
          callId: data.callId,
          transcript: "Call initiated, transcript will be available after call completes",
          answersSummary: {
            strengths: [],
            weaknesses: [],
            skillMatches: [],
            culture: "",
            availability: "",
            compensation: "",
            overallRecommendation: "yes"
          },
          duration: 0,
          callStatus: 'completed'
        };
      } else {
        // Use simulation mode for development/testing
        return simulatePhoneScreen(request, setState, simulationIntervalRef, simulatedLinesRef);
      }
    } catch (err) {
      console.error("Error in phone screening:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to conduct phone screening";
      toast.error(errorMessage);
      
      setState(prev => ({ ...prev, status: 'failed' }));
      return null;
    }
  };

  const cancelPhoneScreen = async () => {
    if (simulationIntervalRef.current) {
      window.clearInterval(simulationIntervalRef.current);
      simulationIntervalRef.current = null;
    }
    
    if (callStatusIntervalRef.current) {
      window.clearInterval(callStatusIntervalRef.current);
    }
    
    if (state.callId && state.callId.indexOf('sim-') !== 0 && state.status === 'in-progress') {
      try {
        await supabase.functions.invoke('twilio-integration', {
          body: {
            action: 'endCall',
            callId: state.callId
          }
        });
      } catch (err) {
        console.error("Error ending call:", err);
      }
    }
    
    setState({
      status: 'idle',
      transcript: null,
      liveTranscript: null,
      summary: null,
      callId: null
    });
    
    toast.info("Phone screening canceled");
  };

  return {
    phoneScreening: state,
    conductLivePhoneScreen,
    cancelPhoneScreen,
    isTwilioAvailable: checkTwilioConfiguration
  };
}

function simulatePhoneScreen(
  request: LivePhoneScreenRequest,
  setState: (state: PhoneScreeningState | ((prev: PhoneScreeningState) => PhoneScreeningState)) => void,
  simulationIntervalRef: React.MutableRefObject<number | null>,
  simulatedLinesRef: React.MutableRefObject<string[]>
): LivePhoneScreenResponse {
  console.log("Twilio not configured, using simulation mode");
  
  setState({ 
    status: 'in-progress',
    liveTranscript: "Interviewer: Hello, is this " + request.candidateName + "?",
    transcript: null,
    summary: null,
    callId: `sim-${Date.now()}`
  });
  
  simulatedLinesRef.current = [
    "Candidate: Yes, this is " + request.candidateName + ".",
    "Interviewer: Great! I'm calling from Acme Inc regarding your application for the " + request.jobTitle + " position. Do you have a few minutes to talk?",
    "Candidate: Yes, I have time to talk now.",
    "Interviewer: Great! Can you tell me a bit about your experience with frontend development, particularly with React?",
    "Candidate: I've been working with React for about 3 years now. I've built several web applications using React, Redux for state management, and styled components for styling. I'm comfortable with hooks, context API, and have experience optimizing React apps for performance.",
    "Interviewer: That's great. What about your experience with backend technologies?",
    "Candidate: I have some experience with Node.js and Express. I've built RESTful APIs and integrated them with MongoDB. I'm also familiar with GraphQL, though I haven't used it as extensively as REST.",
    "Interviewer: How do you typically approach testing in your projects?",
    "Candidate: I believe testing is crucial for maintaining code quality. I usually write unit tests with Jest and React Testing Library for components. For more complex features, I add integration tests. I aim for good test coverage but focus on testing critical business logic rather than simple UI elements.",
    "Interviewer: Can you share an example of a challenging technical problem you've solved recently?",
    "Candidate: In my last project, we needed to implement real-time updates for a dashboard with a lot of data points. The initial implementation was causing performance issues. I implemented a virtualized list with windowing to handle the large dataset efficiently, and used WebSockets for real-time updates instead of frequent API polling. This significantly improved performance and user experience.",
    "Interviewer: What's your availability? When could you start if selected?",
    "Candidate: I could start in two weeks after giving notice to my current employer. I'm available for any additional interviews in the meantime.",
    "Interviewer: And what are your salary expectations for this role?",
    "Candidate: Based on my experience and the market rate for this position, I'm looking for something in the range of $100,000 to $120,000 annually, but I'm flexible depending on other aspects of the compensation package.",
    "Interviewer: Thank you for your time today. I'll share my notes with the hiring team, and we'll be in touch soon about next steps.",
    "Candidate: Thank you for the opportunity. I look forward to hearing back."
  ];
  
  let currentLineIndex = 1;
  
  simulationIntervalRef.current = window.setInterval(() => {
    if (currentLineIndex < simulatedLinesRef.current.length) {
      const currentLines = simulatedLinesRef.current.slice(0, currentLineIndex + 1);
      setState(prev => ({
        ...prev,
        liveTranscript: currentLines.join("\n")
      }));
      currentLineIndex++;
    } else {
      if (simulationIntervalRef.current) {
        window.clearInterval(simulationIntervalRef.current);
        simulationIntervalRef.current = null;
      }
      
      setTimeout(() => {
        const fullTranscript = simulatedLinesRef.current.join("\n");
        const answersSummary = {
          strengths: [],
          weaknesses: [],
          skillMatches: [],
          culture: "",
          availability: "",
          compensation: "",
          overallRecommendation: "yes"
        };
        
        setState({
          status: 'completed',
          transcript: fullTranscript,
          liveTranscript: null,
          summary: answersSummary,
          callId: `sim-${Date.now()}`
        });
        
        toast.success("Phone screening completed successfully");
      }, 1000);
    }
  }, 2000);
  
  return {
    callId: `sim-${Date.now()}`,
    transcript: "Simulated transcript",
    answersSummary: {
      strengths: [],
      weaknesses: [],
      skillMatches: [],
      culture: "",
      availability: "",
      compensation: "",
      overallRecommendation: "yes"
    },
    duration: 0,
    callStatus: 'completed'
  };
}
